import { useState } from "react";
import { api } from "../api";
import { BRAND_QUESTIONS } from "../lib/brandQuestions";

// NewProductModal + OnboardingModal artık StudioSidebar'ın ürün switcher'ından
// açılıyor — ayrı bir outer sidebar kolonu kaldırıldı, ürün seçimi/oluşturma
// StudioSidebar'ın üst logo alanına taşındı.

export function extractDriveFolderId(input: string): string {
  // Accept either bare id or a full Drive URL
  const trimmed = input.trim();
  const match =
    trimmed.match(/\/folders\/([a-zA-Z0-9_-]{20,})/) ??
    trimmed.match(/[?&]id=([a-zA-Z0-9_-]{20,})/);
  return match ? match[1]! : trimmed;
}

export function NewProductModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (newId: string) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [folder, setFolder] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const folderId = extractDriveFolderId(folder);
      const res = await api.createProduct({ name, tag, drive_folder_id: folderId });
      await onSaved(res.id);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-lg w-[28rem] max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-lg font-bold mb-4">Yeni Ürün</h2>
        <div className="space-y-3">
          <Field label="Ürün Adı" value={name} onChange={setName} placeholder="BrainUPIQ" />
          <Field
            label="Tag (unique)"
            value={tag}
            onChange={setTag}
            placeholder="biq"
            help="Dosya isimlendirmesinde kullanılır. Kısa, küçük harf, rakam/tire OK. Örn: biq, mlln."
          />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-zinc-600">Drive Klasör</span>
              <button
                type="button"
                className="text-xs text-accent hover:underline"
                onClick={() => setShowHelp((v) => !v)}
              >
                {showHelp ? "yardımı kapat" : "nereden bulurum?"}
              </button>
            </div>
            <input
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              value={folder}
              placeholder="https://drive.google.com/drive/folders/... veya doğrudan ID"
              onChange={(e) => setFolder(e.target.value)}
            />
            <div className="text-xs text-zinc-500 mt-1">
              Tam URL'i yapıştırabilirsin — ID otomatik çıkarılır.
              {folder && extractDriveFolderId(folder) !== folder.trim() && (
                <span className="block text-emerald-600 mt-0.5">
                  ↳ algılanan ID:{" "}
                  <span className="font-mono">{extractDriveFolderId(folder)}</span>
                </span>
              )}
            </div>
            {showHelp && <DriveHelp />}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="px-3 py-1.5 text-sm rounded border" onClick={onClose}>
            İptal
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded bg-accent text-white disabled:opacity-50"
            disabled={busy || !name || !tag || !folder}
            onClick={submit}
          >
            Oluştur
          </button>
        </div>
      </div>
    </div>
  );
}

function DriveHelp() {
  return (
    <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-zinc-700 space-y-2">
      <div className="font-semibold text-zinc-900">Drive klasör ID nasıl bulunur?</div>
      <ol className="list-decimal list-inside space-y-1">
        <li>
          <a
            href="https://drive.google.com/"
            target="_blank"
            rel="noreferrer"
            className="text-accent underline"
          >
            drive.google.com
          </a>
          'a git, ürün için klasör aç (yoksa <span className="font-mono">+ Yeni → Klasör</span>).
        </li>
        <li>Klasörü çift tıklayarak <b>içine gir</b>.</li>
        <li>
          Tarayıcının URL çubuğundaki adres şu formattadır:
          <div className="font-mono bg-white border rounded px-2 py-1 mt-1 break-all">
            drive.google.com/drive/folders/<span className="text-emerald-700">19i_gmt…0Hzwo</span>
          </div>
          Yeşil olan kısım <b>klasör ID</b>'sidir.
        </li>
        <li>
          Tüm URL'i kopyala-yapıştır da yapabilirsin — sistem otomatik çıkarır.
        </li>
      </ol>
      <div className="pt-1 border-t border-amber-200">
        <span className="font-semibold">Önemli:</span> Sistem bu klasöre yazabilmek için{" "}
        <span className="font-mono">akselyamanel@teknasyon.com</span>'a otomatik editor yetkisi verir.
        Senin Drive'ında olmalı (Shared Drive da olur).
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  help?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-zinc-600 mb-1">{label}</span>
      <input
        className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {help && <span className="block text-xs text-zinc-500 mt-1">{help}</span>}
    </label>
  );
}

const ONBOARDING_STEPS = BRAND_QUESTIONS;

export function OnboardingModal({
  productId,
  onClose,
}: {
  productId: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const current = ONBOARDING_STEPS[step]!;
  const isLast = step === ONBOARDING_STEPS.length - 1;

  function setAnswer(val: string) {
    setAnswers((a) => ({ ...a, [current.key]: val }));
  }

  async function next() {
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    // Last step — submit
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string> = {};
      for (const s of ONBOARDING_STEPS) body[s.key] = answers[s.key] ?? "";
      const data = await api.onboardProduct(productId, body);
      if (!data.ok) throw new Error("Hata");
      setDone(true);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-20 p-4">
        <div className="bg-white rounded-xl shadow-xl w-[32rem] p-8 text-center">
          <div className="text-4xl mb-4">✅</div>
          <h2 className="text-lg font-bold mb-2">Brand Guidelines hazır!</h2>
          <p className="text-sm text-zinc-500 mb-6">
            AI tarafından oluşturulan brand guidelines kaydedildi. Ürün Ayarları'ndan düzenleyebilirsin.
          </p>
          <button
            className="px-5 py-2 bg-accent text-white rounded-lg text-sm font-medium"
            onClick={onClose}
          >
            Ayarlara Git
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-20 p-4">
      <div className="bg-white rounded-xl shadow-xl w-[32rem] p-8">
        {/* Progress bar */}
        <div className="flex gap-1 mb-6">
          {ONBOARDING_STEPS.map((_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full"
              style={{ background: i <= step ? "var(--accent, #5E6AD2)" : "#E4E3DD" }}
            />
          ))}
        </div>

        <p className="text-xs text-zinc-400 mb-1">{step + 1} / {ONBOARDING_STEPS.length}</p>
        <h2 className="text-base font-semibold text-zinc-900 mb-4">{current.label}</h2>

        <textarea
          autoFocus
          rows={3}
          className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder={current.placeholder}
          value={answers[current.key] ?? ""}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) next(); }}
        />
        <p className="text-xs text-zinc-400 mt-1">⌘+Enter ile ilerle</p>

        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}

        <div className="flex justify-between mt-6">
          <button
            className="text-sm text-zinc-400 hover:text-zinc-600"
            onClick={step === 0 ? onClose : () => setStep((s) => s - 1)}
          >
            {step === 0 ? "Atla" : "← Geri"}
          </button>
          <button
            className="px-5 py-2 bg-accent text-white rounded-lg text-sm font-medium disabled:opacity-50"
            disabled={busy}
            onClick={next}
          >
            {busy ? "Oluşturuluyor…" : isLast ? "Brand Guidelines Oluştur" : "İleri →"}
          </button>
        </div>
      </div>
    </div>
  );
}
